import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { DataService } from '../../data.service';

/**
 *  PROFILKÉPEK (Microsoft Graph) A LISTÁKBAN – a kiszolgáló `profilkep.js`-ének kliense.
 *
 *  A kép TOKENNEL jön (blobként): egy sima `<img src="/api/...">` nem visz belépési jegyet,
 *  és a kapu 2.3.140 óta elutasítja – ezért nem jött le a fejléc MS-képe sem.
 *
 *  - Csak dolgozói belépésnél és csak bekapcsolt Graph-nál kér bármit (az állapotot egyszer
 *    kérdezi meg intézményenként).
 *  - Egy személy képét a munkamenetben egyszer tölti le; a „nincs képe” is megmarad.
 *  - Egyszerre legfeljebb 4 kérés megy – egy hosszú névsor sem árasztja el a kiszolgálót.
 */
@Injectable({ providedIn: 'root' })
export class ProfilkepService {
  private allapotok = new Map<string, Promise<boolean>>();
  private kepek = new Map<string, Promise<string | null>>();
  private fut = 0;
  private sor: Array<() => void> = [];
  private intezmenyP: Promise<string> | null = null;

  constructor(private http: HttpClient, private data: DataService) {}

  private async intezmeny(): Promise<string> {
    if (!this.intezmenyP) {
      this.intezmenyP = (async () => {
        const mod = await this.data.getFromStorage('loginmode').catch(() => null);
        if (mod && mod !== 'dolgozo') return '';
        const s = await this.data.getFromStorage('sajatIntezmeny').catch(() => null);
        return String(s?.intezmenyId || '').trim();
      })();
      //  Intézményváltás / belépés után újra kell olvasni.
      setTimeout(() => { this.intezmenyP = null; }, 30_000);
    }
    return this.intezmenyP;
  }

  private bekapcsolva(intezmeny: string): Promise<boolean> {
    if (!this.allapotok.has(intezmeny)) {
      this.allapotok.set(intezmeny, firstValueFrom(
        this.http.get<any>(`/api/profilkep/allapot?intezmeny=${encodeURIComponent(intezmeny)}`))
        .then((r) => !!r?.bekapcsolva).catch(() => false));
    }
    return this.allapotok.get(intezmeny)!;
  }

  private hely(): Promise<void> {
    return new Promise((ok) => { if (this.fut < 4) { this.fut++; ok(); } else this.sor.push(ok); });
  }
  private felszabadit(): void {
    const kov = this.sor.shift();
    if (kov) kov(); else this.fut--;
  }

  /** Blob-URL, vagy `null`, ha nincs kép (kikapcsolva, nincs cím, nincs fotó). */
  async kep(tipus: 'tanulo' | 'dolgozo' | 'sajat', azon: string | number | null | undefined): Promise<string | null> {
    const a = String(azon ?? '').trim();
    if (tipus !== 'sajat' && !a) return null;
    const intezmeny = await this.intezmeny();
    if (!intezmeny || !(await this.bekapcsolva(intezmeny))) return null;
    const kulcs = `${intezmeny}|${tipus}|${a.toLowerCase()}`;
    if (!this.kepek.has(kulcs)) {
      const ut = tipus === 'sajat' ? 'sajat' : `${tipus}/${encodeURIComponent(a)}`;
      this.kepek.set(kulcs, (async () => {
        await this.hely();
        try {
          const r = await firstValueFrom(this.http.get(
            `/api/profilkep/${ut}?intezmeny=${encodeURIComponent(intezmeny)}`,
            { responseType: 'blob', observe: 'response' }));
          const blob = r.body;
          return r.status === 200 && blob && blob.size > 0 ? URL.createObjectURL(blob) : null;
        } catch {
          return null;
        } finally {
          this.felszabadit();
        }
      })());
    }
    return this.kepek.get(kulcs)!;
  }

  /** Intézményváltáskor / Graph-beállítás mentésekor. */
  urit(): void {
    this.allapotok.clear();
    for (const p of this.kepek.values()) p.then((u) => { if (u) URL.revokeObjectURL(u); });
    this.kepek.clear();
    this.intezmenyP = null;
  }
}
